import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useMutationState } from "@tanstack/react-query";
import { getMutationKey } from "@trpc/react-query";
import { trpc } from "../../lib/trpc.js";
import { Botao, cx } from "../../components/ui.js";
import {
  estadoDaFaixa,
  estaEsperando,
  eventoDoProgresso,
  msAteSumir,
  terminou,
  tituloDaAba,
  type EstadoDaFaixa,
  type Evento,
  type Fase,
  type PedidoObservado,
  type Tinta,
  type Tom,
} from "./progresso.js";

/**
 * A faixa de progresso dos marcadores: presa logo abaixo da barra do app, com as três faixas em
 * miniatura sendo "pintadas" enquanto a IA trabalha.
 *
 * Criar os três leva ~40 s e o botão que se aperta fica no fim de um formulário longo: quem o aperta
 * não vê o topo da página. A faixa vai com a pessoa na rolagem. O resto da tela fica quieto de
 * propósito — o único movimento é a tinta —, e tudo o que ela diz sai de `progresso.ts`.
 */

/** A tira em si: tinta cheia, trilha na mesma cor a 20%. As classes são literais para o Tailwind achá-las. */
const TINTAS = ["bg-tinta-ambar", "bg-tinta-pergaminho", "bg-tinta-azul-noite"] as const;
const TRILHAS = ["bg-tinta-ambar/20", "bg-tinta-pergaminho/20", "bg-tinta-azul-noite/25"] as const;

/** Cada tira pinta no seu ritmo e começa num ponto do ciclo — assim nunca descem juntas. */
const RITMOS = [
  { ritmo: "5.6s", atraso: "0s" },
  { ritmo: "6.5s", atraso: "-2.2s" },
  { ritmo: "5s", atraso: "-3.7s" },
] as const;

function Tira({ tinta, indice }: { tinta: Tinta; indice: number }) {
  const { ritmo, atraso } = RITMOS[indice % RITMOS.length]!;
  const estilo = {
    "--nivel": tinta.nivel,
    "--ritmo": ritmo,
    // Pintando: a defasagem do ciclo. Chegando: uma escadinha curta, da primeira à última.
    "--atraso": tinta.chegando ? `${indice * 0.12}s` : atraso,
  } as CSSProperties;

  return (
    <span className={cx("fita relative block h-14 w-3.5", TRILHAS[indice % TRILHAS.length])}>
      <span
        style={estilo}
        className={cx(
          "tinta absolute inset-0",
          TINTAS[indice % TINTAS.length],
          tinta.situacao === "pintando" && "tinta-pintando",
          tinta.situacao === "cheia" && tinta.chegando && "tinta-chegando",
        )}
      />
    </span>
  );
}

const BANDA: Record<Tom, string> = {
  calmo: "border-borda bg-superficie",
  ambar: "border-brasa/40 bg-brasa-fraca",
  erro: "border-descartar/50 bg-descartar-fraca",
};

const TITULO: Record<Tom, string> = {
  calmo: "text-texto",
  ambar: "text-brasa",
  erro: "text-texto",
};

const TEXTO: Record<Tom, string> = {
  calmo: "text-texto-suave",
  ambar: "text-texto",
  erro: "text-texto",
};

/** Segundos desde `desde`; só anda enquanto espera, e fecha a conta no instante em que a espera acaba. */
function useSegundosDecorridos(desde: number | null, esperando: boolean): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (desde === null) return;
    setAgora(Date.now());
    if (!esperando) return;
    const id = setInterval(() => setAgora(Date.now()), 250);
    return () => clearInterval(id);
  }, [desde, esperando]);
  return desde === null ? 0 : Math.max(0, Math.floor((agora - desde) / 1000));
}

function useAbaVisivel(): boolean {
  const [visivel, setVisivel] = useState(() => !document.hidden);
  useEffect(() => {
    const mudou = () => setVisivel(!document.hidden);
    document.addEventListener("visibilitychange", mudou);
    return () => document.removeEventListener("visibilitychange", mudou);
  }, []);
  return visivel;
}

/**
 * O título da aba acompanha a geração e, se ela termina com a aba escondida, avisa até a pessoa
 * voltar. Devolve o título que a página tinha, ao desmontar.
 */
function useTituloDaAba(fase: Fase, indice: number | undefined) {
  const original = useRef<string | null>(null);
  const fimNaoVisto = useRef(false);

  useEffect(() => {
    original.current = document.title;
    return () => {
      document.title = original.current ?? document.title;
    };
  }, []);

  useEffect(() => {
    if (fase === "ocioso") fimNaoVisto.current = false;
    else if (terminou(fase) && document.hidden) fimNaoVisto.current = true;

    const aplicar = () => {
      if (!document.hidden) fimNaoVisto.current = false;
      document.title = tituloDaAba(fase, fimNaoVisto.current, indice) ?? original.current ?? "";
    };
    aplicar();
    document.addEventListener("visibilitychange", aplicar);
    return () => document.removeEventListener("visibilitychange", aplicar);
  }, [fase, indice]);
}

/**
 * O que a faixa mostra agora: junta `marcadores.gerar` (que a página tem) e `marcadores.refazer` (que
 * mora no `FolhaDeMarcadores`) sem levantar o estado de lá — a chamada fica no cache de mutações, e
 * de lá se lê o que está acontecendo, com o índice do marcador que foi pedido.
 */
export function useEventoDoProgresso(gerar: PedidoObservado): Evento {
  const [montadoEm] = useState(() => Date.now());
  const refeitos = useMutationState({
    filters: { mutationKey: getMutationKey(trpc.marcadores.refazer) },
    select: (m): PedidoObservado => ({
      estado: m.state.status,
      enviadoEm: m.state.submittedAt,
      indice: (m.state.variables as { indice?: number } | undefined)?.indice ?? 0,
      ...(m.state.error && { mensagem: (m.state.error as Error).message }),
    }),
  });
  const refazer = refeitos.reduce<PedidoObservado | null>(
    (a, b) => (a === null || b.enviadoEm > a.enviadoEm ? b : a),
    null,
  );
  return eventoDoProgresso(gerar, refazer, montadoEm);
}

export function FaixaDeProgresso({
  evento,
  aoVer,
  aoRepetir,
}: {
  evento: Evento;
  /** Leva a pessoa ao resultado — o conjunto já pronto, ou o que faltou nele. */
  aoVer: () => void;
  /** Refaz o pedido que falhou. */
  aoRepetir: () => void;
}) {
  const esperando = estaEsperando(evento.fase);
  const segundos = useSegundosDecorridos(evento.desde, esperando);
  const abaVisivel = useAbaVisivel();
  useTituloDaAba(evento.fase, evento.indice);

  const estado = estadoDaFaixa({
    fase: evento.fase,
    segundos,
    ...(evento.entregues !== undefined && { entregues: evento.entregues }),
    ...(evento.faltam && { faltam: evento.faltam }),
    ...(evento.indice !== undefined && { indice: evento.indice }),
    ...(evento.mensagem && { mensagem: evento.mensagem }),
  });

  const [fechada, setFechada] = useState<string | null>(null);
  const [parada, setParada] = useState(false);
  const aberta = estado !== null && fechada !== evento.chave;

  // A pausa é de uma ocorrência só: se a faixa some com o mouse em cima, o "saiu do mouse" não chega.
  useEffect(() => setParada(false), [evento.chave]);

  // O que deu certo sai sozinho. O relógio só corre com a aba à vista e para enquanto a pessoa está
  // com o mouse ou o foco na faixa: ninguém deve ver o botão desaparecer debaixo do dedo.
  const sumirEm = msAteSumir(evento.fase);
  useEffect(() => {
    if (sumirEm === null || !aberta || !abaVisivel || parada) return;
    const id = setTimeout(() => setFechada(evento.chave), sumirEm);
    return () => clearTimeout(id);
  }, [sumirEm, aberta, abaVisivel, parada, evento.chave]);

  // Ao fechar, o conteúdo fica até a altura terminar de encolher: sem isto a faixa esvaziaria antes.
  const ultimo = useRef<EstadoDaFaixa | null>(null);
  useEffect(() => {
    if (aberta) ultimo.current = estado;
  });
  const exibido = aberta ? estado : ultimo.current;

  // A altura da faixa, para quem rola até o resultado não ficar com o começo dele por baixo dela.
  const banda = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const raiz = document.documentElement;
    const el = banda.current;
    if (!aberta || !el) return;
    const medir = () => raiz.style.setProperty("--altura-da-faixa", `${el.offsetHeight}px`);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => {
      observador.disconnect();
      raiz.style.removeProperty("--altura-da-faixa");
    };
  }, [aberta]);

  const fechavel = exibido !== null && !estaEsperando(evento.fase);
  const acao = exibido?.acao ?? null;

  function agir() {
    if (acao?.tipo === "repetir") return aoRepetir();
    aoVer();
    // O aviso de "pronto" já cumpriu o papel: quem foi ver o conjunto não precisa dele no topo.
    if (sumirEm !== null) setFechada(evento.chave);
  }

  return (
    <>
      {/* Sempre montada, e só com o começo e o fim: o leitor de tela não deve ouvir o cronômetro. */}
      <div role="status" aria-live="polite" className="sr-only">
        {estado?.anuncio ?? ""}
      </div>

      {/* Presa sob a barra do app (no celular) ou no topo (onde não há barra). A altura anima de 0 a
          cheia em vez de pular, e o conteúdo da página desce junto, sem salto. */}
      <div
        className={cx(
          "sticky top-(--altura-da-barra) z-10 -mx-4 grid transition-[grid-template-rows] duration-200 ease-out",
          aberta
            ? "visible grid-rows-[1fr]"
            : "invisible grid-rows-[0fr] [transition-property:grid-template-rows,visibility] [transition-delay:0s,200ms]",
        )}
      >
        <div inert={!aberta} className="min-h-0 overflow-hidden">
          {exibido && (
            <section
              ref={banda}
              aria-label="Progresso dos marcadores"
              onPointerEnter={(e) => e.pointerType === "mouse" && setParada(true)}
              onPointerLeave={() => setParada(false)}
              onFocus={(e) => e.target.matches(":focus-visible") && setParada(true)}
              onBlur={() => setParada(false)}
              className={cx("relative border-b px-4 py-3", BANDA[exibido.tom])}
            >
              <div className="flex items-start gap-3.5 sm:items-center">
                <div aria-hidden className="flex shrink-0 gap-1 self-center">
                  {exibido.tintas.map((t, i) => (
                    <Tira key={i} tinta={t} indice={i} />
                  ))}
                </div>

                <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    {/* No celular o cronômetro vai à ponta direita da coluna; onde há largura, fica ao lado do título,
                        que é de quem ele fala. */}
                    <div
                      className={cx(
                        "flex items-baseline justify-between gap-3 sm:justify-start sm:gap-4",
                        fechavel && "pr-8 sm:pr-0",
                      )}
                    >
                      <p
                        className={cx(
                          "font-display text-[1.0625rem] leading-tight tracking-[-0.01em]",
                          TITULO[exibido.tom],
                        )}
                      >
                        {exibido.titulo}
                      </p>
                      {exibido.tempo && (
                        <span
                          role="timer"
                          aria-live="off"
                          className="num shrink-0 text-[0.9375rem] font-medium text-texto"
                        >
                          {exibido.tempo}
                        </span>
                      )}
                    </div>
                    <p
                      className={cx(
                        "mt-1 text-[0.8125rem] leading-snug wrap-anywhere",
                        TEXTO[exibido.tom],
                      )}
                    >
                      {exibido.texto}
                    </p>
                  </div>

                  {acao && (
                    <div className="mt-1.5 shrink-0 sm:mt-0 sm:pr-10">
                      <Botao tom="quieto" className="min-h-11 px-3.5" onClick={agir}>
                        {acao.rotulo}
                      </Botao>
                    </div>
                  )}
                </div>
              </div>

              {fechavel && (
                <button
                  type="button"
                  aria-label="Fechar o aviso"
                  onClick={() => setFechada(evento.chave)}
                  className="absolute top-1 right-1.5 grid size-11 place-items-center rounded-peca text-texto-suave hover:text-texto"
                >
                  <svg viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor">
                    <path d="m5 5 10 10M15 5 5 15" strokeWidth="1.75" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </section>
          )}
          {/* O respiro entre a faixa e o formulário vive aqui dentro: fora, ele sobraria quando a faixa fecha. */}
          <div aria-hidden className="h-6" />
        </div>
      </div>
    </>
  );
}
